import { ChevronDown, CircleDollarSign, Info } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { Collapsible, CollapsibleContent } from '~/components/ui/collapsible';
import { ProductClassesBreakdown } from '~/features/product-groups/components/product-classes-breakdown';
import { useCanSeeCost, useShowCostAmounts } from '~/hooks/useCanSeeCost';
import { cn } from '~/utils/cn';
import { diffInMs, formatDateTime, formatDurationMs, nowISO } from '~/utils/date';

import { useCostActual, useCostEstimate } from '../hooks/use-cost-estimate';
import type {
  CostActual,
  CostActualPhase,
  CostEstimate,
  PlannedCost,
} from '../types/cost-estimate.types';
import { decidedViaLabel } from '../utils/decision-labels';
import { formatUsd } from '../utils/format-usd';

import { CostActualProducts, CostActualValue } from './cost-actual-summary';
import { CostAuditMeta } from './cost-audit-meta';
import { CostDecisionActions } from './cost-decision-actions';
import { ApprovalModeBadge, CostScenarioSummary } from './cost-scenarios';
import { CostStatusChip } from './cost-status-chip';
import { CostTotalDetails, CostTotalValue } from './cost-total-summary';

interface CostEstimateCardProps {
  /** Workflow id of the gated run; the card renders nothing while it is null. */
  workflowId: string | null | undefined;
  /** Host run is live (true) or ended (false): the estimate polls while the run is live and
   *  the gate is undecided, and stops once the run ends. Omit when the host cannot tell. */
  runActive?: boolean | undefined;
  /** Legacy host signal ("live and pre-enrichment"): true keeps polling a missing estimate,
   *  exactly as before. Ignored when `runActive` is passed. */
  awaitingEstimate?: boolean;
  /** Two-column stats for narrow hosts (e.g. the trial loader card). */
  compact?: boolean;
  /**
   * Start as a one-line strip (title, est. total, product count, status chip) that
   * expands to the full card. Pending approvals start expanded so Approve / Reject show.
   */
  collapsible?: boolean;
  /**
   * Host run's enrichment phase: absent until enrichment starts (no actual-cost fetch),
   * `running` polls the actual every 60s, `done` fetches it a final time.
   */
  enrichmentPhase?: CostActualPhase | null;
  /** The cost planned at creation (batch header). Shown first, and on its own until the gate
   *  has an estimate — so a just-started batch is not blank. Lily.ai only. */
  plannedCost?: PlannedCost | null;
  className?: string;
  /** `data-testid` root; stats, toggle and decision buttons derive from it. */
  testId?: string;
}

/**
 * Pre-enrichment cost gate summary for one full-catalog run: status, product
 * count, per-product and total USD estimate, thresholds, what the gate decided
 * (approval tier / auto-proceed), and Approve / Reject when the caller may decide.
 * Renders for every status, BELOW_THRESHOLD included — it is how users see the
 * estimate before generation. Renders nothing when no estimate exists or the gate
 * is switched off (SKIPPED), so a disabled gate leaves run pages as they were.
 * When amounts are hidden (`amountsHidden: true`, or no flag and a customer viewer) it is a
 * "Cost approval" card: status, products, sample and "being reviewed by Lily" while it waits.
 */
export function CostEstimateCard({
  workflowId,
  runActive,
  awaitingEstimate = false,
  compact = false,
  collapsible = false,
  enrichmentPhase = null,
  plannedCost = null,
  className,
  testId = 'cost-estimate-card',
}: CostEstimateCardProps) {
  const canSeeCost = useCanSeeCost();
  // Planned amounts are null for callers who may not see cost; the local rule hides them too.
  const plannedUsd = canSeeCost ? (plannedCost?.totalUsd ?? null) : null;
  const { data: estimate } = useCostEstimate(workflowId, {
    runActive: runActive ?? (awaitingEstimate ? true : undefined),
  });
  // Actual spend only exists once enrichment has started; the host decides when that is.
  const { data: actual } = useCostActual(workflowId, {
    enabled: enrichmentPhase !== null,
    poll: enrichmentPhase === 'running',
  });
  if (!workflowId || !estimate || estimate.status === 'SKIPPED') {
    return plannedUsd !== null ? (
      <PlannedCostCard plannedUsd={plannedUsd} className={className} testId={testId} />
    ) : null;
  }
  return (
    <CostEstimateCardView
      estimate={estimate}
      plannedUsd={plannedUsd}
      actual={enrichmentPhase !== null ? (actual ?? null) : null}
      actualRunning={enrichmentPhase === 'running'}
      workflowId={workflowId}
      compact={compact}
      collapsible={collapsible}
      className={className}
      testId={testId}
    />
  );
}

/**
 * Before the gate has an estimate (it is recorded after the sample), the cost the stepper planned
 * at creation, so a just-started batch shows its cost straight away.
 */
function PlannedCostCard({
  plannedUsd,
  className,
  testId,
}: {
  plannedUsd: number;
  className: string | undefined;
  testId: string;
}) {
  const { t } = useTranslation('common');
  return (
    <Card data-testid={testId} className={cn('flex w-full flex-col gap-1 px-5 py-3', className)}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <CircleDollarSign className="text-muted-foreground h-4 w-4" aria-hidden="true" />
        <h3 data-testid={`${testId}-title`} className="text-foreground font-semibold">
          {t('costEstimate.title')}
        </h3>
        <span aria-hidden="true" className="text-muted-foreground">
          ·
        </span>
        <span data-testid={`${testId}-planned`} className="text-foreground font-semibold">
          {t('costEstimate.planned.value', { total: formatUsd(plannedUsd) })}
        </span>
      </div>
      <p data-testid={`${testId}-planned-note`} className="text-muted-foreground text-xs">
        {t('costEstimate.planned.beforeEstimate')}
      </p>
    </Card>
  );
}

interface CostEstimateCardViewProps {
  estimate: CostEstimate;
  /** The cost planned at creation, when stored and visible; shown ahead of the estimate. */
  plannedUsd: number | null;
  /** Actual spend so far; null before enrichment starts or while nothing is recorded. */
  actual: CostActual | null;
  /** Enrichment still running — the actual is labelled "so far". */
  actualRunning: boolean;
  /** The host's run id — the key the card reads the estimate by, so a decision writes it. */
  workflowId: string;
  compact: boolean;
  collapsible: boolean;
  className: string | undefined;
  testId: string;
}

/** Card body — split out so the decision actions (and their hook) only mount with an estimate. */
function CostEstimateCardView({
  estimate,
  plannedUsd,
  actual,
  actualRunning,
  workflowId,
  compact,
  collapsible,
  className,
  testId,
}: CostEstimateCardViewProps) {
  const { t } = useTranslation('common');
  const isPending = estimate.status === 'PENDING_APPROVAL';
  const showAmounts = useShowCostAmounts(estimate.amountsHidden);
  // Actual spend is lily.ai-only (customers never fetch it), but a server verdict hides it too.
  const shownActual = showAmounts ? actual : null;

  // Collapsible hosts start as a strip, except when a decision is waiting. A poll
  // that flips the gate INTO pending re-expands it (state adjusted during render),
  // so Approve / Reject never land hidden behind the strip.
  const [isExpanded, setExpanded] = useState(!collapsible || isPending);
  const [prevStatus, setPrevStatus] = useState(estimate.status);
  if (prevStatus !== estimate.status) {
    setPrevStatus(estimate.status);
    if (isPending) setExpanded(true);
  }
  const showBody = !collapsible || isExpanded;
  const toggleExpanded = () => {
    setExpanded((v) => !v);
  };

  return (
    <Card
      data-testid={testId}
      className={cn(
        'flex w-full flex-col transition-[padding] duration-200',
        showBody ? 'p-5' : 'px-5 py-3',
        className,
      )}
    >
      {/* Title + status chip. On collapsible hosts the whole row toggles the body;
          the chevron Button is the focusable control and its click bubbles here. */}
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-2',
          collapsible && 'cursor-pointer',
        )}
        {...(collapsible ? { onClick: toggleExpanded } : {})}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <CircleDollarSign className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          <h3 data-testid={`${testId}-title`} className="text-foreground text-sm font-semibold">
            {showAmounts ? t('costEstimate.title') : t('costEstimate.customer.title')}
          </h3>
          {!showBody ? (
            <CollapsedSummary
              estimate={estimate}
              plannedUsd={showAmounts ? plannedUsd : null}
              actual={shownActual}
              showAmounts={showAmounts}
              testId={`${testId}-summary`}
            />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <CostStatusChip status={estimate.status} testId={`${testId}-status-chip`}>
            <StatusLabel estimate={estimate} customer={!showAmounts} />
          </CostStatusChip>
          {showAmounts ? <ApprovalModeBadge estimate={estimate} /> : null}
          {collapsible ? (
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground h-7 w-7 cursor-pointer"
              aria-expanded={isExpanded}
              data-testid={`${testId}-toggle-button`}
              aria-label={
                isExpanded ? t('costEstimate.strip.collapse') : t('costEstimate.strip.expand')
              }
            >
              <ChevronDown
                className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
                aria-hidden="true"
              />
            </Button>
          ) : null}
        </div>
      </div>

      {/* Body — Radix animates its height (and skips the animation on first mount,
          so always-open hosts render exactly as before). */}
      <Collapsible open={showBody}>
        <CollapsibleContent>
          <div className="flex flex-col gap-4 pt-4">
            {showAmounts && plannedUsd !== null ? (
              <p data-testid={`${testId}-planned`} className="text-muted-foreground text-sm">
                {t('costEstimate.planned.label')}{' '}
                <span className="text-foreground font-semibold tabular-nums">
                  {formatUsd(plannedUsd)}
                </span>
              </p>
            ) : null}
            {estimate.status === 'ERROR' ? (
              <p data-testid={`${testId}-error`} className="text-muted-foreground text-sm">
                {estimate.error ?? t('costEstimate.errorFallback')}
              </p>
            ) : (
              <div
                className={cn(
                  'grid gap-4',
                  compact || !showAmounts
                    ? 'grid-cols-2'
                    : shownActual
                      ? 'grid-cols-2 sm:grid-cols-3'
                      : 'grid-cols-2 sm:grid-cols-4',
                )}
              >
                <Stat
                  label={t('costEstimate.productsToProcess')}
                  testId={`${testId}-products-value`}
                >
                  {estimate.productCount.toLocaleString()}
                </Stat>
                <Stat label={t('costEstimate.sampleSize')} testId={`${testId}-sample-value`}>
                  {t('costEstimate.sampled', {
                    estimated: estimate.estimatedProducts,
                    sample: estimate.sampleSize,
                  })}
                </Stat>
                {showAmounts ? (
                  <>
                    <Stat
                      label={t('costEstimate.perProductAvg')}
                      testId={`${testId}-per-product-value`}
                    >
                      {estimate.perProduct ? formatUsd(estimate.perProduct.avgUsd) : '—'}
                    </Stat>
                    <Stat label={t('costEstimate.estimatedTotal')} testId={`${testId}-total-value`}>
                      <CostTotalValue total={estimate.total} />
                    </Stat>
                  </>
                ) : null}
                {shownActual ? (
                  <>
                    <Stat
                      label={actualRunning ? t('costActual.actualSoFar') : t('costActual.actual')}
                      testId={`${testId}-actual-value`}
                    >
                      <CostActualValue actual={shownActual} proRate={actualRunning} />
                    </Stat>
                    <Stat
                      label={t('costActual.productsProcessed')}
                      testId={`${testId}-processed-value`}
                    >
                      <CostActualProducts
                        actual={shownActual}
                        estimatedCount={estimate.productCount}
                      />
                    </Stat>
                  </>
                ) : null}
              </div>
            )}

            {/* What the products are, by group: the sync's classes and those carried in. */}
            <ProductClassesBreakdown
              testId={`${testId}-product-classes`}
              included={estimate.productClasses}
              carriedIn={estimate.carriedIn ?? null}
              adjustment={estimate.classAdjustment ?? null}
              className="border-t pt-4"
            />

            {showAmounts && estimate.status !== 'ERROR' ? (
              <CostScenarioSummary estimate={estimate} />
            ) : null}

            {showAmounts ? <CostTotalDetails total={estimate.total} /> : null}

            {/* Who asked for the run, where, when — and who decided, with any notes. */}
            <CostAuditMeta
              estimate={estimate}
              className={cn('border-t pt-4', compact && 'sm:grid-cols-2')}
            />

            {/* Thresholds the total was compared against */}
            {showAmounts ? (
              <p data-testid={`${testId}-thresholds`} className="text-muted-foreground text-xs">
                {t('costEstimate.thresholds', {
                  alert:
                    estimate.alertThresholdUsd === null
                      ? t('costEstimate.noThreshold')
                      : formatUsd(estimate.alertThresholdUsd),
                  approval:
                    estimate.approvalThresholdUsd === null
                      ? t('costEstimate.noThreshold')
                      : formatUsd(estimate.approvalThresholdUsd),
                })}
              </p>
            ) : null}

            {showAmounts ? (
              <GateNote estimate={estimate} testId={`${testId}-gate-note`} />
            ) : (
              <CustomerGateNote estimate={estimate} testId={`${testId}-gate-note`} />
            )}

            {showAmounts && isPending && estimate.expiresAt ? (
              <ExpiryCountdown expiresAt={estimate.expiresAt} testId={`${testId}-expiry`} />
            ) : null}

            {showAmounts && !estimate.calibrated && estimate.status !== 'ERROR' ? (
              <div
                data-testid={`${testId}-uncalibrated`}
                className="bg-muted text-muted-foreground flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
              >
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{t('costEstimate.uncalibrated')}</span>
              </div>
            ) : null}

            {/* Always mounted (it shows its buttons only while pending and decidable): a poll that
                ends the pending state must not unmount an open dialog or drop the decision's
                callbacks. Keyed by the host's workflowId, which is what this card reads. */}
            <CostDecisionActions
              estimate={estimate}
              workflowId={workflowId}
              testId={`${testId}-decision`}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

/** Strip-mode headline metrics: est. total, actual (once recorded) and product count —
 *  product count only without amounts. Nothing for ERROR — the chip says it. */
function CollapsedSummary({
  estimate,
  plannedUsd,
  actual,
  showAmounts,
  testId,
}: {
  estimate: CostEstimate;
  plannedUsd: number | null;
  actual: CostActual | null;
  showAmounts: boolean;
  testId: string;
}) {
  const { t } = useTranslation('common');
  if (estimate.status === 'ERROR') return null;
  return (
    <span
      data-testid={testId}
      className="text-muted-foreground animate-in fade-in flex flex-wrap items-center gap-x-2 text-sm tabular-nums duration-200"
    >
      {plannedUsd !== null ? (
        <>
          <span aria-hidden="true">·</span>
          <span data-testid={`${testId}-planned`} className="text-foreground font-semibold">
            {t('costEstimate.planned.value', { total: formatUsd(plannedUsd) })}
          </span>
        </>
      ) : null}
      {showAmounts && estimate.total ? (
        <>
          <span aria-hidden="true">·</span>
          <span className="text-foreground font-semibold">
            {t('costEstimate.strip.estTotal', { total: formatUsd(estimate.total.avgUsd) })}
          </span>
        </>
      ) : null}
      {actual ? (
        <>
          <span aria-hidden="true">·</span>
          <span className="text-foreground font-semibold">
            {t('costActual.stripActual', { total: formatUsd(actual.totalUsd) })}
          </span>
        </>
      ) : null}
      <span aria-hidden="true">·</span>
      <span>{t('costEstimate.strip.productCount', { count: estimate.productCount })}</span>
    </span>
  );
}

/**
 * Human status line, naming the decider and channel once the gate is decided. `customer`
 * words the automatic outcomes without thresholds or alerts ("Continued") and a pending gate
 * without who approves it.
 */
export function StatusLabel({
  estimate,
  customer = false,
}: {
  estimate: CostEstimate;
  customer?: boolean;
}) {
  const { t } = useTranslation('common');
  if (customer) {
    if (estimate.status === 'BELOW_THRESHOLD' || estimate.status === 'ALERTED') {
      return t('costEstimate.customer.statusContinued');
    }
    if (estimate.status === 'PENDING_APPROVAL') return t('costEstimate.status.pending');
  }
  // The backend-resolved name reads better than the raw email; fall back to the stored actor.
  const by = estimate.decidedByName ?? estimate.decidedBy;
  const via = decidedViaLabel(t, estimate.decidedVia);
  switch (estimate.status) {
    case 'SKIPPED':
      return t('costEstimate.status.skipped');
    case 'BELOW_THRESHOLD':
      return t('costEstimate.status.belowThreshold');
    case 'ALERTED':
      return t('costEstimate.status.alerted');
    case 'PENDING_APPROVAL':
      return estimate.approvalTier === 'APPROVERS'
        ? t('costEstimate.status.pendingApprovers')
        : t('costEstimate.status.pending');
    case 'APPROVED':
      if (by && via) return t('costEstimate.status.approvedByVia', { by, via });
      return by ? t('costEstimate.status.approvedBy', { by }) : t('costEstimate.status.approved');
    case 'REJECTED':
      return by ? t('costEstimate.status.rejectedBy', { by }) : t('costEstimate.status.rejected');
    case 'EXPIRED':
      return t('costEstimate.status.expired');
    case 'ERROR':
      return t('costEstimate.status.error');
    case 'CANCELLED':
      return t('costEstimate.status.cancelled');
  }
}

/**
 * One line explaining what the gate did with this run: who must approve a
 * pending estimate (SELF = the requester, APPROVERS = designated approvers), or
 * why it proceeded automatically. Nothing for decided / skipped / error states —
 * the status chip already says it.
 */
export function GateNote({
  estimate,
  testId = 'cost-gate-note',
}: {
  estimate: CostEstimate;
  /** `data-testid` for the note line. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  let text: string | null = null;
  let attention = false;
  switch (estimate.status) {
    case 'PENDING_APPROVAL':
      attention = true;
      if (estimate.approvalTier === 'APPROVERS') {
        text = estimate.canDecide
          ? t('costEstimate.gate.approversCanDecide')
          : t('costEstimate.gate.approversWaiting');
      } else {
        text = estimate.canDecide
          ? t('costEstimate.gate.selfCanDecide')
          : t('costEstimate.gate.selfWaiting');
      }
      break;
    case 'ALERTED':
      text = estimate.attended
        ? t('costEstimate.gate.alerted')
        : t('costEstimate.gate.alertedScheduled');
      break;
    case 'BELOW_THRESHOLD':
      text = t('costEstimate.gate.belowThreshold');
      break;
    default:
      break;
  }
  if (!text) return null;
  return (
    <p
      data-testid={testId}
      className={cn('text-sm', attention ? 'text-warning font-medium' : 'text-muted-foreground')}
    >
      {text}
    </p>
  );
}

/**
 * The customer's one line on the gate, with no amounts or limits: while pending, Lily is
 * reviewing the cost; once it let the run through, that it continued. Nothing otherwise — the
 * status chip says it.
 */
export function CustomerGateNote({
  estimate,
  testId = 'cost-gate-note',
}: {
  estimate: CostEstimate;
  /** `data-testid` for the note line. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const key =
    estimate.status === 'PENDING_APPROVAL'
      ? 'costEstimate.customer.reviewing'
      : estimate.status === 'ALERTED' || estimate.status === 'BELOW_THRESHOLD'
        ? 'costEstimate.customer.continued'
        : null;
  if (!key) return null;
  return (
    <p
      data-testid={testId}
      className={cn(
        'text-sm',
        estimate.status === 'PENDING_APPROVAL'
          ? 'text-warning font-medium'
          : 'text-muted-foreground',
      )}
    >
      {t(key)}
    </p>
  );
}

/** Live "auto-rejects in …" line; re-renders every 30s while the gate is pending. */
export function ExpiryCountdown({
  expiresAt,
  testId = 'cost-expiry-countdown',
}: {
  expiresAt: string;
  /** `data-testid` for the countdown line. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const [now, setNow] = useState(nowISO);
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(nowISO());
    }, 30_000);
    return () => {
      window.clearInterval(id);
    };
  }, []);
  const remaining = diffInMs(now, expiresAt);
  return (
    <p data-testid={testId} className="text-warning text-sm">
      {remaining > 0
        ? t('costEstimate.expiresIn', {
            duration: formatDurationMs(remaining),
            at: formatDateTime(expiresAt),
          })
        : t('costEstimate.expiringNow')}
    </p>
  );
}

/** Caption over a value — the card's stat cell. */
function Stat({ label, children, testId }: { label: string; children: ReactNode; testId: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {label}
      </span>
      <span data-testid={testId} className="text-foreground text-sm font-medium tabular-nums">
        {children}
      </span>
    </div>
  );
}
